import React, { useState, useEffect } from "react";
import { FiMoreHorizontal } from "react-icons/fi";
import SkillCard from "../../components/Common/CommonCard";
import ReusableTable from "../../components/Common/BaseTable";
import newMessageIcon from "../../assets/newMessage.png";
import marketingIcon from "../../assets/marketing.png";
import productRatingIcon from "../../assets/productRating.png";
import socialMediaIcon from "../../assets/socialMedia.png";
import talkingToAIIcon from "../../assets/talkingToAI.png";
import chatIcon from "../../assets/chat.png";
import { Col, Row } from "react-bootstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Eye, Pencil, Upload, Trash2, UploadIcon } from 'lucide-react';
import Breadcrumb from "../../components/Common/Bredcrump";
import BaseButton from "../../components/Common/BaseButton";
import axiosInstance from "../../utils/api";
import Loader from "../../components/Common/Loader";
import UnifiedDocumentModal from "../../components/Common/UnifiedDocumentModal";

const Skill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [selectedRow, setSelectedRow] = useState(null);
  const [apiSkills, setApiSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingAgentRoute, setCheckingAgentRoute] = useState(false); // Loading state while checking URL route
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
    total_pages: 1,
    total_count: 0
  });
  const [selectedAgent, setSelectedAgent] = useState(null); // Track selected DIY agent
  const [contentData, setContentData] = useState([]); // Content posts for selected agent
  const [contentLoading, setContentLoading] = useState(false);
  const [contentPagination, setContentPagination] = useState({
    page: 1,
    per_page: 5,
    total_pages: 1,
    total_count: 0
  });
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  const handleSearch = (searchValue) => {
    console.log("Search:", searchValue);
  };

  const handleDelete = (selectedRows) => {
    
  };

  const handleDuplicate = (selectedRows) => {
    console.log("Duplicate:", selectedRows);
  };

  const handleMenuOption = (action, rowData) => {
    console.log(`${action} clicked for`, rowData);
    setSelectedRow(null);
    
    if (action === 'preview' && rowData.id) {
      // Navigate to preview screen
      navigate(`/skills/diy-agent/preview/${rowData.id}`);
    } else if (action === 'edit' && rowData.id && selectedAgent) {
      // Navigate to playground with post ID and agent data
      const agentSlug = selectedAgent.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      navigate(`/skills/${agentSlug}/playground`, {
        state: {
          postId: rowData.id,
          agentData: selectedAgent.fullData,
          editMode: true
        }
      });
    }
  };

  const skills = [
    { title: "Email", icon: newMessageIcon },
    { title: "Blog", icon: talkingToAIIcon },
    { title: "Social", icon: socialMediaIcon },
    { title: "Product Description", icon: productRatingIcon },
    { title: "Performance Ads", icon: marketingIcon },
    { title: "Free Form Content", icon: talkingToAIIcon },
    { title: "Chat", icon: chatIcon },
  ];

  // Default data for hardcoded skills
  const defaultData = [
    {
      name: "Content 1",
      created: "2023-06-27",
      skillType: "Email", 
      status: "Published",
    },
    {
      name: "Content 2",
      created: "2023-08-16",
      skillType: "Blog",
      status: "Draft",
    },
  ];

  const columns = [
    {
      id: "selection",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={() => table.toggleAllPageRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={() => row.toggleSelected()}
        />
      ),
      size: "5%",
    },
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "created",
      header: "Created",
    },
    {
      accessorKey: "skillType",
      header: "Skill Type",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue();
        const statusClass =
          status === "Published" ? "status-published" : "status-draft";
        const dotColor = status === "Published" ? "green" : "grey";
        return (
          <span className={`status-badge ${statusClass}`}>
            <span style={{ color: dotColor, marginRight: '5px' }}>•</span>
            {status}
          </span>
        );
      },
    },
    {
      header: "Actions",
      cell: ({ row }) => {
        return (
          <div style={{ position: 'relative' }}>
            <FiMoreHorizontal 
              className="three-dot-menu" 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRow(selectedRow === row.index ? null : row.index);
              }}
              style={{ cursor: 'pointer' }}
            />
            {selectedRow === row.index && (
              <div className="action-menu">
                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('preview', row.original);
                  }}
                >
                  <Eye size={16} />
                  <span>Preview</span>
                </button>
                
                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('edit', row.original);
                  }}
                >
                  <Pencil size={16} />
                  <span>Edit</span>
                </button>

                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('document', row.original);
                  }}
                >
                  <UploadIcon size={16} />
                  <span>Document</span>
                </button>
                
                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('publish', row.original);
                  }}
                >
                  <Upload size={16} />
                  <span>Publish</span>
                </button>
                
                <button 
                  className="menu-item delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('delete', row.original);
                  }}
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const fetchApiSkills = async (page = 1, size = 10,is_publish = true) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/v1/diy_agents', {
        params: {
          page,
          size,
          is_publish
        }
      });
      
      if (response.data && response.data.success) {
        // Filter to only show agents where is_publish is true
        const publishedAgents = (response.data.data || []).filter(agent => agent.is_publish === true);
        setApiSkills(publishedAgents);
        setPagination(response.data.pagination || {
          page: 1,
          size: 10,
          total_pages: 1,
          total_count: publishedAgents.length // Use filtered count
        });
      }
    } catch (error) {
      console.error('Error fetching API skills:', error);
      setApiSkills([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiSkills(1, 10);
  }, []);

  // Check URL path for selected agent on mount and when path changes
  useEffect(() => {
    const pathname = location.pathname;
    
    // Check if we're on a hardcoded route (exact matches or sub-routes)
    const hardcodedRoutes = [
      '/skills/email',
      '/skills/blog', 
      '/skills/chat',
      '/skills/product-description',
      '/skills/free-form-content'
    ];
    
    const isHardcodedRoute = hardcodedRoutes.some(route => 
      pathname === route || pathname.startsWith(route + '/')
    );
    
    // If on base /skills route, reset
    if (pathname === '/skills') {
      setSelectedAgent(null);
      setContentData([]);
      setCheckingAgentRoute(false);
      return;
    }
    
    // If on a hardcoded route, reset
    if (isHardcodedRoute) {
      setSelectedAgent(null);
      setContentData([]);
      setCheckingAgentRoute(false);
      return;
    }
    
    // If on /skills/{something}, check if it's a DIY agent
    if (pathname.startsWith('/skills/')) {
      // Extract agent slug from path (e.g., /skills/agent-name -> agent-name)
      const pathParts = pathname.split('/');
      const agentSlug = pathParts[pathParts.length - 1];
      
      if (agentSlug && agentSlug !== 'skills') {
        // If apiSkills are loaded, find the agent by slug
        if (apiSkills.length > 0) {
          setCheckingAgentRoute(false);
          const agent = apiSkills.find(skill => {
            const skillSlug = skill.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            return skillSlug === agentSlug;
          });
          
          if (agent) {
            setSelectedAgent({ id: agent.id, name: agent.name, fullData: agent }); // Store full agent data
            // Call API with agent name
            fetchContentPosts(agent.name, 1, contentPagination.per_page);
          } else {
            // Agent not found, reset
            setSelectedAgent(null);
            setContentData([]);
          }
        } else {
          // If apiSkills not loaded yet, set loading state
          setCheckingAgentRoute(true);
        }
      }
    } else {
      setCheckingAgentRoute(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, apiSkills]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      fetchApiSkills(newPage, pagination.size);
    }
  };

  const handleSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 1 }));
    fetchApiSkills(1, newSize);
  };

  // Fetch content posts for a DIY agent
  const fetchContentPosts = async (agentName, page = 1, perPage = 5, search = '') => {
    try {
      setContentLoading(true);
      // Replace spaces with underscores for API
      const agentNameForApi = agentName.replace(/\s+/g, '_');
      const response = await axiosInstance.get('/api/v1/content_posts', {
        params: {
          post_type: agentNameForApi,
          search: search || '',
          page,
          per_page: perPage
        }
      });
      
      if (response.data) {
        // Handle both response structures: {content_posts: [], meta: {}} or {data: [], pagination: {}}
        const posts = response.data.content_posts || response.data.data || [];
        const meta = response.data.meta || response.data.pagination || {};
        
        // Transform the response data to match table structure
        const transformedData = posts.map((post) => ({
          name: post.title || post.name || 'Untitled',
          created: post.created_at ? new Date(post.created_at).toISOString().split('T')[0] : (post.created || new Date().toISOString().split('T')[0]),
          skillType: agentName,
          status: post.status ? post.status.charAt(0).toUpperCase() + post.status.slice(1) : 'Draft',
          ...post // Include all original data for actions
        }));
        
        setContentData(transformedData);
        
        // Update pagination from meta object
        if (meta.total_count !== undefined) {
          setContentPagination({
            page: page || 1, // Use the page we requested, not what API returns
            per_page: meta.per_page || perPage,
            total_pages: meta.total_pages || Math.ceil(meta.total_count / (meta.per_page || perPage)),
            total_count: meta.total_count
          });
        } else if (response.data.pagination) {
          setContentPagination({
            ...response.data.pagination,
            page: page || 1 // Ensure we use the requested page
          });
        }
      }
    } catch (error) {
      console.error('Error fetching content posts:', error);
      setContentData([]);
    } finally {
      setContentLoading(false);
    }
  };

  const handleSkillClick = (title, skillId = null, agentName = null) => {
    // Handle DIY agent clicks (from API) - navigate to route like /skills/agent-name
    if (skillId && agentName) {
      // Create a URL-friendly slug from agent name (same format as hardcoded routes)
      const agentSlug = agentName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      // Navigate to skills page with agent name in route (like hardcoded skills navigate)
      navigate(`/skills/${agentSlug}`);
      return;
    }

    // Handle hardcoded skill clicks - reset to default behavior
    setSelectedAgent(null);
    setContentData([]);
    
    if (title === "Email") {
      navigate('/skills/email');
    } else if (title === "Product Description") {
      navigate('/skills/product-description');
    }
    else if (title === "Blog"){
      navigate('/skills/blog');
    }
    else if (title === "Free Form Content"){
      navigate('/skills/free-form-content');
    }
    else if (title === "Chat"){
      navigate('/skills/chat');
    }
  };

  return (
    <div style={{ position: 'relative' }}>
    <Breadcrumb />
    
      {(loading || checkingAgentRoute) && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          minHeight: '200px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Loader />
            <div style={{ marginTop: '10px' }}>
              {checkingAgentRoute ? 'Loading agent...' : 'Loading skills...'}
            </div>
          </div>
        </div>
      )}
      <div
        className="skills-container"
        style={{ display: "flex", gap: "16px", marginTop: "5px", flexWrap: "wrap", opacity: (loading || checkingAgentRoute) ? 0.5 : 1 }}
      >
        {/* If an agent is selected, show only that agent, otherwise show all */}
        {selectedAgent ? (
          // Show only the selected agent
          <SkillCard
            key={selectedAgent.id}
            icon={talkingToAIIcon}
            title={selectedAgent.name}
            onClick={() => handleSkillClick(selectedAgent.name, selectedAgent.id, selectedAgent.name)}
          />
        ) : (
          <>
            {/* Hardcoded Skills */}
            {skills.map((skill, index) => (
              <SkillCard
                key={skill?.title}
                icon={skill.icon}
                title={skill.title}
                onClick={() => handleSkillClick(skill.title)}
              />
            ))}
            
            {/* API Skills */}
            {apiSkills.map((skill) => (
              <SkillCard
                key={skill.id}
                icon={talkingToAIIcon}
                title={skill.name}
                onClick={() => handleSkillClick(skill.name, skill.id, skill.name)}
              />
            ))}
          </>
        )}
      </div>
      
      {/* Pagination for API Skills - only show when no agent is selected */}
      {apiSkills.length > 0 && !selectedAgent && (
        <div className="pagination d-flex justify-content-between align-items-center mt-3 mb-4" style={{ position: 'relative' }}>
          
          <div className="rows-per-page d-flex align-items-center gap-2">
            <span>Show rows:</span>
            <select
              className="form-select"
              style={{ width: '70px' }}
              value={pagination.size}
              onChange={(e) => handleSizeChange(Number(e.target.value))}
              disabled={loading}
            >
              {[5, 10, 15, 25].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="pagination-info">
            {((pagination.page - 1) * pagination.size + 1)}-
            {Math.min(pagination.page * pagination.size, pagination.total_count)} of {pagination.total_count}
          </div>

          <div className="pagination-buttons d-flex gap-1">
            <button
              className="btn btn-outline-secondary"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
            >
              {"<"}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages || loading}
            >
              {">"}
            </button>
          </div>
        </div>
      )}
      <div className="headertop d-flex justify-content-between align-items-center">
        <div className="title-table-header">Generated Content</div>
        {selectedAgent && (
          <button 
            className="btn text-light" 
            style={{backgroundColor:'var(--primary)'}}
            onClick={() => setShowDocumentModal(true)}
          >
            + Generate 
          </button>
        )}
      </div>
      <div className="mt-3">
        {contentLoading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader />
            <div style={{ marginTop: '10px' }}>Loading content...</div>
          </div>
        )}
        {!contentLoading && (
          <ReusableTable
            data={selectedAgent ? contentData : defaultData}
            columns={columns}
            onSearch={handleSearch}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            gridView={false}
            isLoading={contentLoading}
            // Add pagination for content posts if agent is selected
            {...(selectedAgent && {
              useServerPagination: true,
              page: contentPagination.page,
              perPage: contentPagination.per_page,
              total: contentPagination.total_count,
              onPageChange: (newPage) => {
                setContentPagination(prev => ({ ...prev, page: newPage }));
                fetchContentPosts(selectedAgent.name, newPage, contentPagination.per_page);
              },
              onPerPageChange: (newPerPage) => {
                setContentPagination(prev => ({ ...prev, per_page: newPerPage, page: 1 }));
                fetchContentPosts(selectedAgent.name, 1, newPerPage);
              }
            })}
          />
        )}
      </div>
      
      {/* UnifiedDocumentModal for DIY agents */}
      {selectedAgent && (
        <UnifiedDocumentModal
          show={showDocumentModal}
          onHide={() => setShowDocumentModal(false)}
          initialTags=""
          initialDocList=""
          agentName={selectedAgent.name} // Pass agent name to modal
          agentData={selectedAgent.fullData} // Pass full agent data to access folder ID
        />
      )}
    </div>
  );
};

export default Skill;
